import { useState } from "react";
import { Button, Dialog } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import RadioButton from "@rio-cloud/rio-uikit/RadioButton";
import { Occurrence } from "../../../models/occurrence";
import SuccessDialog from "../../dialogs/SuccessDialog";
import ErrorDialog from "../../dialogs/ErrorDialog";

interface IFinalizationReasonDialogProps {
  showDialog: boolean;
  onClose: () => void;
  onConfirm: (reasonType: string, reasonDescription: string) => void;
  loading?: boolean;
}

// Opções pré-definidas de justificativas
const REASON_OPTIONS = [
  { value: "TOW_TRUCK_UNAVAILABLE", label: "Indisponibilidade de serviço de reboque", labelId: "intl-msg:kanban.finalizationReasonDialog.reasonTowTruckUnavailable" },
  { value: "TOW_TRUCK_DEALERSHIP", label: "Reboque acionado direto pela concessionária", labelId: "intl-msg:kanban.finalizationReasonDialog.reasonTowTruckDealership" },
  { value: "CUSTOMER_OWN_MEANS", label: "Cliente resolveu por meios próprios ou não deseja mais o serviço", labelId: "intl-msg:kanban.finalizationReasonDialog.reasonCustomerOwnMeans" },
  { value: "OTHER", label: "Outro (especificar)", labelId: "intl-msg:kanban.finalizationReasonDialog.reasonOther" },
];

export const FinalizeOccurrenceButton = (
  { occurrence, finishOccurrence, disabledAllFields, persona, stepId }:
    { occurrence?: Occurrence | null, finishOccurrence?: (occurrence: Occurrence, reasonType: string, reasonDescription: string, fromStepId?: number) => Promise<boolean>, disabledAllFields?: boolean, persona?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP', stepId?: number }
) => {
  const intl = useIntl();
  const [showFinalizationModal, setShowFinalizationModal] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  return (
    <>
      {persona === 'TOWER' ? 
        <button
        className={`btn btn-info ml-2 ${finishLoading ? "btn-loading disabled" : ""}`}
        type="button"
        onClick={() => setShowFinalizationModal(true)}
        disabled={finishLoading || disabledAllFields}
      >
        {intl.formatMessage({ id: "intl-msg:occurrence.card.finishAction" })}
      </button> : null}

      <FinalizationReasonDialog
        showDialog={showFinalizationModal}
        onClose={() => setShowFinalizationModal(false)}
        onConfirm={async (reasonType, reasonDescription) => {
          setShowFinalizationModal(false);
          setFinishLoading(true);

          if (!occurrence) {
            setShowErrorDialog(true);
            return;
          }

          try {
            await finishOccurrence?.(occurrence, reasonType, reasonDescription, stepId) ?? false;
          } catch (error) {
            setShowErrorDialog(true);
            return;
          }

          setShowSuccessDialog(true);
          setFinishLoading(false);
        }}
        loading={finishLoading}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.successTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.successBody" })}
      />

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.errorTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.errorBody" })}
      />
    </>
  )
}

const FinalizationReasonDialog = ({
  showDialog,
  onClose,
  onConfirm,
  loading = false,
}: IFinalizationReasonDialogProps) => {
  const intl = useIntl();
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customDescription, setCustomDescription] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleConfirm = () => {
    if (!selectedReason) {
      setError(intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.selectReasonError" }));
      return;
    }

    if (selectedReason === "OTHER" && !customDescription.trim()) {
      setError(intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.customReasonError" }));
      return;
    }

    setError("");
    const finalDescription = selectedReason === "OTHER"
      ? customDescription.trim()
      : REASON_OPTIONS.find(option => option.value === selectedReason)?.label || "";

    onConfirm(selectedReason, finalDescription);
  };

  const handleClose = () => {
    setSelectedReason("");
    setCustomDescription("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      show={showDialog}
      title={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.title" })}
      body={
        <div className="space-y-4">
          <div>
            <p className="text-gray-600 dark:text-neutral-400 mb-4">
              {intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.description" })}
            </p>
          </div>

          <div className="space-y-3">
            <label className="block font-medium text-gray-700 dark:text-neutral-300">
              {intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.reasonLabel" })}
            </label>

            <div className="space-y-2">
              {REASON_OPTIONS.map((option) => (
                <RadioButton
                  key={option.value}
                  name="finalizationReason"
                  checked={selectedReason === option.value}
                  onChange={() => setSelectedReason(option.value)}
                  className="mt-1"
                >
                  <span className="text-gray-700 dark:text-neutral-300">{intl.formatMessage({ id: option.labelId })}</span>
                </RadioButton>
              ))}
            </div>
          </div>

          {selectedReason === "OTHER" && (
            <div className="space-y-2">
              <label className="block font-medium text-gray-700 dark:text-neutral-300">
                {intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.customReasonLabel" })}
              </label>
              <textarea
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder={intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.customReasonPlaceholder" })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                maxLength={500}
              />
              <div className="text-gray-500 dark:text-neutral-400">
                {intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.charCount" }, { count: customDescription.length })}
              </div>
            </div>
          )}

          {error && (
            <div className="text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              {error}
            </div>
          )}
        </div>
      }
      footer={
        <div className="display-flex justify-content-end">
          <Button
            bsStyle="default"
            onClick={handleClose}
            className="margin-right-10"
            disabled={loading}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.card.cancel" })}
          </Button>
          <Button
            bsStyle="primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.finishing" }) : intl.formatMessage({ id: "intl-msg:kanban.finalizationReasonDialog.finishOccurrence" })}
          </Button>
        </div>
      }
      bsSize={Dialog.SIZE_MD}
      onClose={handleClose}
    />
  );
};

export default FinalizationReasonDialog;
